import { takeEvery, put, select, take, call } from "redux-saga/effects";
import moment from "moment";
import { addTimingMeasureInstructionsToAction } from "utils/timing";
import {
  selectAccountId,
  selectAccountMetaSynced
} from "reducers/account/meta";
import * as firebase from "sagas/firebase/lib";

import {
  UPDATE_STEP_IN_STORE,
  CANCEL_ACCOUNT_SYNC
} from "constants/actionTypes";
import { SUPPORT_EMAIL } from "constants/externalAppcuesResources";
import { MANAGED_TAG, TagAction, UPDATED_TAGS_ON_STEP } from "constants/events";
import { trackEvent } from "actions/events";
import { selectUserId } from "reducers/user";
import {
  selectAccountTags,
  selectAccountTagsMeta
} from "reducers/account/tags";
import {
  FETCH_ACCOUNT_TAGS,
  CREATE_TAG,
  DELETE_TAG,
  EDIT_TAG,
  UPDATE_STEP_TAGS,
  UPDATE_TAGS_MANAGEMENT_STATE
} from "constants/account/tags";
import { syncFirebasePath } from "sagas/firebase/internal-lib";
import { REPLACE_ACCOUNT_META } from "constants/account/meta";
import {
  setAccountTagsErrored,
  setAccountTagsFetching,
  replaceAccountTags
} from "actions/account/tags";

import { update as updateFlow } from "actions/account/flows";
import { promisaga } from "utils/as-promised";

function* fetchAccountTagsSaga(/* action */) {
  try {
    const accountMetaSynced = yield select(selectAccountMetaSynced);

    if (!accountMetaSynced) {
      yield take(REPLACE_ACCOUNT_META);
    }

    const accountId = yield select(selectAccountId);
    const accountCategoriesMeta = yield select(selectAccountTagsMeta);
    const { fetching, synced, errored } = accountCategoriesMeta;

    if (errored || (!synced && !fetching)) {
      yield put(setAccountTagsFetching());

      yield call(
        syncFirebasePath,
        `accounts/${accountId}/tags`,
        replaceAccountTagsSaga,
        CANCEL_ACCOUNT_SYNC
      );
    }
  } catch (e) {
    yield put(setAccountTagsErrored(e));
  }
}

export function* replaceAccountTagsSaga(data) {
  yield put(
    addTimingMeasureInstructionsToAction(
      replaceAccountTags(data),
      FETCH_ACCOUNT_TAGS
    )
  );
}

export function* createTagInFirebase(tag) {
  const accountId = yield select(selectAccountId);
  return yield call(firebase.push, { [`accounts/${accountId}/tags`]: tag });
}

export function* removeTagInFirebase(tagId) {
  const accountId = yield select(selectAccountId);
  return yield call(firebase.remove, [`accounts/${accountId}/tags/${tagId}`]);
}

export function* editTagInFirebase(tagId, updatedTag) {
  const accountId = yield select(selectAccountId);

  const tag = {
    ...updatedTag,
    id: tagId
  };

  return yield call(firebase.update, {
    [`accounts/${accountId}/tags/${tagId}`]: tag
  });
}

function* createTag(action) {
  const { tagName } = action.payload;
  try {
    const createdAt = moment().valueOf();
    const createdBy = yield select(selectUserId);

    const tagId = yield call(
      createTagInFirebase,
      newTagModel(tagName, createdAt, createdBy)
    );

    yield put(
      trackEvent(MANAGED_TAG, {
        tagAction: TagAction.CREATED_TAG,
        tagId,
        createdBy
      })
    );

    return tagId;
  } catch (error) {
    console.error(`could not create tag with name ${tagName}: `, error);
    return undefined;
  }
}

function* updateTagsOnStep(action) {
  const {
    stepId,
    changes: { tags }
  } = action.payload;

  try {
    yield put(
      updateFlow(stepId, {
        tags
      })
    );
  } catch (error) {
    console.error(`could not update tags for step ${stepId}`, error);
  }
}

function* deleteTag(action) {
  const { tagId } = action.payload;
  const deletedBy = yield select(selectUserId);
  const tags = yield select(selectAccountTags);

  try {
    if (tags[tagId]) {
      yield call(removeTagInFirebase, tagId);
      const tagName = tags[tagId].name;
      const successMessage = `You have successfully deleted the tag "${tagName}".`;
      yield put({
        type: UPDATE_TAGS_MANAGEMENT_STATE,
        payload: {
          errorMessage: null,
          successMessage
        }
      });

      yield put(
        trackEvent(MANAGED_TAG, {
          tagAction: TagAction.DELETED_TAG,
          tagId,
          deletedBy
        })
      );
    } else {
      throw `tagId (${tagId}) not found on account`;
    }
  } catch (error) {
    yield put({
      type: UPDATE_TAGS_MANAGEMENT_STATE,
      payload: {
        errorMessage: `We're sorry - we had trouble deleting this tag. Could you please try again or contact ${SUPPORT_EMAIL}`,
        successMessage: null
      }
    });
    console.error(`could not delete tag with id ${tagId}: `, error);
  }
}

function* editTag(action) {
  const { tagId, newTagName } = action.payload;

  const updatedAt = moment().valueOf();
  const updatedBy = yield select(selectUserId);
  const updatedTag = updatedTagModel(tagId, newTagName, updatedAt, updatedBy);

  try {
    yield call(editTagInFirebase, tagId, updatedTag);

    yield put(
      trackEvent(MANAGED_TAG, {
        tagAction: TagAction.EDITED_TAG,
        tagId,
        updatedBy
      })
    );
  } catch (error) {
    console.error(
      `(tagId=${tagId}, newTagName=${newTagName}): could not edit tag: `,
      error
    );
  }
}

function newTagModel(tagName, createdAt, createdBy) {
  return {
    name: tagName,
    createdAt,
    createdBy
  };
}

function updatedTagModel(tagId, tagName, updatedAt, updatedBy) {
  return {
    id: tagId,
    name: tagName,
    updatedAt,
    updatedBy
  };
}

function* trackTagUpdatesOnStep(action) {
  const {
    stepId,
    announcementId,
    changes: { tags }
  } = action.payload;
  if (!tags) return;

  yield put(
    trackEvent(UPDATED_TAGS_ON_STEP, {
      stepId: stepId || announcementId,
      numberOfTags: Object.keys(tags).length
    })
  );
}

function* tags() {
  yield takeEvery(FETCH_ACCOUNT_TAGS, fetchAccountTagsSaga);
  yield takeEvery(CREATE_TAG, promisaga(createTag));
  yield takeEvery(UPDATE_STEP_TAGS, updateTagsOnStep);
  yield takeEvery(DELETE_TAG, deleteTag);
  yield takeEvery(EDIT_TAG, editTag);
  yield takeEvery(UPDATE_STEP_IN_STORE, trackTagUpdatesOnStep);
}

export default tags;
